import dayjs from 'dayjs';
import { sample } from 'lodash';
import type { DemoReleasePayload } from '@/actions/ui';
import {
    AppPlatform,
    type AutomationRun,
    type Release,
    ReleaseCreatedOrigin,
    ReleaseStatus,
    ReleaseStepStatus,
    ReleaseStepType,
    ReleaseTimelinePhase,
    ReleaseType,
    TimelineEvent,
} from '@/types';
import type { Destination } from '@/types/destinations';
import type { DefaultStepRunStatusData } from '@/types/stepRunStatus';
import type { WorkflowStepGraph } from '@/types/workflow';
import type { StepArchetypeGroup, StepGroup } from '@/types/workflowRunStepRunGraph';
import { generateReleaseOverview, generateReleaseStatsHistory } from './releaseStatsHistory';
import { getMockedStatusForStepRun } from './releaseSteps';
import {
    generateStepStatusUpdatedEvent,
    generateTimelineEventForBatchSubmit,
    generateTimelineEventsHistory,
} from './timelineEventsHistory';

const mockWorkflowRelease = (
    appId: string,
    version: string,
    releaseType: ReleaseType,
    destinations: Destination[],
    workflowStepGraph: WorkflowStepGraph,
    timelinePhase: ReleaseTimelinePhase,
    releaseSummaryMessage: string,
    workflowRunStatus: ReleaseStatus = ReleaseStatus.Active,
): Release => {
    const releaseId = `${appId}:${version}`;
    const workflowRunId = `wfrun-${version}`;
    const isReleaseComplete = timelinePhase === ReleaseTimelinePhase.Completed;

    const stepRunGraph = {
        legacyReleaseId: releaseId,
        stepGroups: workflowStepGraph.stepGroups.map(
            (group): StepGroup => ({
                stepArchetypeGroups: group.archetypeGroups.map((archetypeGroup): StepArchetypeGroup => {
                    const stepStatus = getMockedStatusForStepRun(
                        archetypeGroup.archetype,
                        timelinePhase,
                        workflowRunStatus,
                    );
                    return {
                        id: archetypeGroup.name,
                        name: archetypeGroup.name,
                        archetype: archetypeGroup.archetype,
                        combinedStatus: stepStatus,
                        stepRunStatusDatum: archetypeGroup.steps.map((step, stepIndex): DefaultStepRunStatusData => {
                            return {
                                stepConfigId: step.stepConfigId,
                                stepRunId: `step-${step.stepConfigId}-${version}`,
                                name: step.stepName || `${archetypeGroup.name} ${stepIndex + 1}`,
                                status: stepStatus,
                                statusReasonString: '',
                                hasEnabledLifecycleAutomation: false,
                                hasEnabledPendingLifecycleAutomation: false,
                                hasEnabledFailedLifecycleAutomation: false,
                                hasEnabledBlockedLifecycleAutomation: false,
                                pendingChecklistItemCount: 0,
                                type: undefined,
                                extraData: null,
                            };
                        }),
                    };
                }),
            }),
        ),
    };

    const allEvents: TimelineEvent[] = [];
    for (const stepGroup of stepRunGraph.stepGroups) {
        for (const archetypeGroup of stepGroup.stepArchetypeGroups) {
            for (const stepRun of archetypeGroup.stepRunStatusDatum) {
                if (![ReleaseStepStatus.Inactive, ReleaseStepStatus.Pending].includes(stepRun.status)) {
                    const stepStatusEvent = generateStepStatusUpdatedEvent(
                        appId,
                        stepRun.stepRunId,
                        releaseId,
                        version,
                        archetypeGroup.archetype,
                        stepRun.status,
                    );
                    allEvents.push(stepStatusEvent);
                }
            }
        }
    }

    allEvents.push(
        generateTimelineEventForBatchSubmit({
            releaseId,
            version,
            workflowRunId,
            appId,
            stepRunGraph,
            workflowStepGraph,
        }),
        ...generateTimelineEventsHistory({
            appId,
            version,
            currentUser: null,
            isReleaseComplete,
            platform: destinations[0]?.platform,
            appStoreType: destinations[0]?.appStoreType,
        }),
    );

    // Sorting events by timestamp (most recent first)
    allEvents.sort((a, b) => new Date(b.triggeredAt).getTime() - new Date(a.triggeredAt).getTime());

    // Randomize the number of events by filtering some out
    const keepRatio = 0.6 + Math.random() * 0.4;
    const timelineEventsHistory = allEvents.filter(() => Math.random() < keepRatio);

    // Generate variable timestamps based on timeline phase to make releases unique
    const daysOffset = Math.floor(Math.random() * 3) + 1; // 1-3 days variation
    const hoursOffset = Math.floor(Math.random() * 8); // 0-7 hours variation

    const timestamps = {
        targetKickoffDate: dayjs()
            .subtract(4 + daysOffset, 'days')
            .subtract(hoursOffset, 'hours')
            .toISOString(),
        targetSubmitDate: dayjs()
            .add(2 + daysOffset, 'days')
            .add(hoursOffset, 'hours')
            .toISOString(),
        targetReleaseDate: dayjs()
            .add(5 + daysOffset, 'days')
            .add(hoursOffset, 'hours')
            .toISOString(),
        createdAt: dayjs()
            .subtract(1, 'hour')
            .subtract(hoursOffset * 10, 'minutes')
            .toISOString(),
        updatedAt: dayjs().subtract(15, 'minute').subtract(hoursOffset, 'minutes').toISOString(),
        startedAt:
            timelinePhase !== ReleaseTimelinePhase.Upcoming ?
                dayjs()
                    .subtract(3 + daysOffset, 'days')
                    .subtract(hoursOffset, 'hours')
                    .toISOString()
            :   undefined,
        kickedOffAt:
            timelinePhase !== ReleaseTimelinePhase.Upcoming ?
                dayjs()
                    .subtract(2 + daysOffset, 'days')
                    .subtract(hoursOffset, 'hours')
                    .toISOString()
            :   undefined,
        submittedAt:
            (
                (timelinePhase === ReleaseTimelinePhase.Current || timelinePhase === ReleaseTimelinePhase.Completed) &&
                workflowRunStatus !== ReleaseStatus.Skipped
            ) ?
                dayjs()
                    .subtract(3 + Math.floor(Math.random() * 2), 'hour')
                    .subtract(hoursOffset * 5, 'minutes')
                    .toISOString()
            :   undefined,
        releasedAt:
            timelinePhase === ReleaseTimelinePhase.Completed && workflowRunStatus !== ReleaseStatus.Skipped ?
                dayjs()
                    .subtract(1, 'hour')
                    .subtract(hoursOffset * 3, 'minutes')
                    .toISOString()
            :   undefined,
        skippedAt:
            workflowRunStatus === ReleaseStatus.Skipped ?
                dayjs()
                    .subtract(1, 'hour')
                    .subtract(hoursOffset * 2, 'minutes')
                    .toISOString()
            :   undefined,
    };

    const isCompletedRelease = timelinePhase === ReleaseTimelinePhase.Completed;

    return {
        ...timestamps,
        id: releaseId,
        releaseId,
        type: releaseType,
        status: workflowRunStatus,
        version,
        workflowRunId,
        displayableVersion: version,
        nextVersion: version,
        displayableNextVersion: version,
        timelinePhase,
        isReleaseTagged: true,
        timelineEventsHistory,
        createdOrigin: ReleaseCreatedOrigin.AutoCreatedByRunway,
        shouldWarnAboutNewerCommits: false,
        releasePilotId: sample(['ssullenberger@demo.com', 'aearhart@demo.com', 'narmstrong@demo.com']),
        doesBranchExist: true,
        isStaticReleaseBranch: false,
        scheduledAutomationsPaused: false,
        lifecycleAutomations: [] as AutomationRun[],
        canBeSubmitted: false,
        canRevertCompletedStatus: false,
        phasedReleaseHasBeenSupersededByNewRelease: false,
        shouldMatchPreviousPhasedRelease: false,
        isKickedOff: !!timestamps.kickedOffAt,
        isSubmitted: !!timestamps.submittedAt,
        isReleased: !!timestamps.releasedAt,
        releaseSummaryMessage: { message: releaseSummaryMessage },
        releaseOverview: generateReleaseOverview({
            kickOffDate: timestamps.kickedOffAt,
            submitDate: timestamps.submittedAt,
            releaseDate: timestamps.releasedAt,
            targetKickoffDate: timestamps.targetKickoffDate,
            targetSubmitDate: timestamps.targetSubmitDate,
            targetReleaseDate: timestamps.targetReleaseDate,
        }),
        releaseStatsHistory: generateReleaseStatsHistory({
            version,
            platform: destinations[0]?.platform || AppPlatform.Ios,
            isCompletedRelease,
        }),
        overview: {
            codeContributors: [
                {
                    id: '1',
                    firstName: 'Sully',
                    lastName: 'Sullenberger',
                    email: 'ssullenberger@demo.com',
                    avatar: '',
                    itemOfWorkCount: 15,
                },
                {
                    id: '2',
                    firstName: 'Amelia',
                    lastName: 'Earhart',
                    email: 'aearhart@demo.com',
                    avatar: '',
                    itemOfWorkCount: 12,
                },
                {
                    id: '3',
                    firstName: 'Neil',
                    lastName: 'Armstrong',
                    email: 'narmstrong@demo.com',
                    avatar: '',
                    itemOfWorkCount: 8,
                },
            ],
            contributingTeams: [{ name: 'Engineering', itemOfWorkCount: 3 }],
            topChangedFiles: [
                {
                    fileName: 'src/features/checkout/CheckoutFlow.tsx',
                    linesChangedCount: 145,
                    isConfigFile: false,
                },
                {
                    fileName: 'src/features/auth/LoginScreen.tsx',
                    linesChangedCount: 89,
                    isConfigFile: false,
                },
                {
                    fileName: 'src/components/common/Button.tsx',
                    linesChangedCount: 67,
                    isConfigFile: false,
                },
            ],
            totalFilesChanged: 47,
            previousReleasesAdoptionRate: undefined,
            averageAppStoreCrashFreeSessions: 99.2,
            averageAppStoreCrashFreeUsers: 99.5,
            averageBetaCrashFreeSessions: 98.8,
            averageBetaCrashFreeUsers: 99.1,
            totalAutomationsPerformedCount: 5,
            totalAutomationsScheduledCount: 8,
            estimatedAutomationsTimeSaved: 7200,
            estimatedAutomationsCostSaving: 12000,
            releaseConfidenceScore: undefined,
        },

        // workflows
        stepRunGraph,
        destinationSummaries: destinations.map((dest) => ({
            id: dest.stepConfigId,
            displayName: dest.name,
            stepConfigId: dest.stepConfigId,
            platform: dest.platform,
            appStoreType: dest.appStoreType,
            iconURL: dest.iconURL,
            archetype: ReleaseStepType.Takeoff,
            destinationStatus: ReleaseStatus.Active,
        })),
        featureFlags: [],
    };
};

export const mockCrossPlatformAppReleases = (
    appId: string,
    destinations: Destination[],
    workflowStepGraph: WorkflowStepGraph,
): Release[] => [
    mockWorkflowRelease(
        appId,
        '1.4.0',
        ReleaseType.Minor,
        destinations,
        workflowStepGraph,
        ReleaseTimelinePhase.Upcoming,
        'Preparing for our next big feature update with improved performance',
        ReleaseStatus.Active,
    ),
    mockWorkflowRelease(
        appId,
        '1.3.0',
        ReleaseType.Minor,
        destinations,
        workflowStepGraph,
        ReleaseTimelinePhase.Current,
        'New dark mode feature and accessibility improvements are live',
        ReleaseStatus.Active,
    ),
    mockWorkflowRelease(
        appId,
        '1.2.0',
        ReleaseType.Minor,
        destinations,
        workflowStepGraph,
        ReleaseTimelinePhase.Completed,
        'Rolling out enhanced user experience with streamlined checkout flow',
        ReleaseStatus.Completed,
    ),
    mockWorkflowRelease(
        appId,
        '1.1.1',
        ReleaseType.Hotfix,
        destinations,
        workflowStepGraph,
        ReleaseTimelinePhase.Completed,
        'Quick fix for login authentication issue affecting select users',
        ReleaseStatus.Completed,
    ),
    mockWorkflowRelease(
        appId,
        '1.1.0',
        ReleaseType.Minor,
        destinations,
        workflowStepGraph,
        ReleaseTimelinePhase.Completed,
        'Social sharing integration was postponed for further testing',
        ReleaseStatus.Skipped,
    ),
    mockWorkflowRelease(
        appId,
        '1.0.0',
        ReleaseType.Minor,
        destinations,
        workflowStepGraph,
        ReleaseTimelinePhase.Completed,
        'Major milestone reached with full production launch',
        ReleaseStatus.Completed,
    ),
    mockWorkflowRelease(
        appId,
        '0.9.0',
        ReleaseType.Minor,
        destinations,
        workflowStepGraph,
        ReleaseTimelinePhase.Completed,
        'Beta testing phase extended to gather more user feedback',
        ReleaseStatus.Skipped,
    ),
    mockWorkflowRelease(
        appId,
        '0.8.0',
        ReleaseType.Minor,
        destinations,
        workflowStepGraph,
        ReleaseTimelinePhase.Completed,
        'Merged with parallel development branch for unified release',
        ReleaseStatus.Skipped,
    ),
    mockWorkflowRelease(
        appId,
        '0.7.0',
        ReleaseType.Minor,
        destinations,
        workflowStepGraph,
        ReleaseTimelinePhase.Completed,
        'Successfully delivered core functionality with payment integration',
        ReleaseStatus.Completed,
    ),
];

export const mockWhiteLabelAppReleases = (
    appId: string,
    destinations: Destination[],
    workflowStepGraph: WorkflowStepGraph,
): Release[] => [
    // 8.7.0 - Upcoming
    mockWorkflowRelease(
        appId,
        '8.7.0',
        ReleaseType.Minor,
        destinations,
        workflowStepGraph,
        ReleaseTimelinePhase.Upcoming,
        'Unified booking experience across all airline destinations',
        ReleaseStatus.Active,
    ),
    // 8.6.0 - In Progress (Current)
    mockWorkflowRelease(
        appId,
        '8.6.0',
        ReleaseType.Minor,
        destinations,
        workflowStepGraph,
        ReleaseTimelinePhase.Current,
        'Multi-destination loyalty program integration rolling out now',
        ReleaseStatus.Active,
    ),
    // 8.5.0 - Completed (Rolling out)
    mockWorkflowRelease(
        appId,
        '8.5.0',
        ReleaseType.Minor,
        destinations,
        workflowStepGraph,
        ReleaseTimelinePhase.Completed,
        'Real-time flight tracking and notifications for all destinations',
        ReleaseStatus.Completed,
    ),
    // 8.4.1 - Hotfix
    mockWorkflowRelease(
        appId,
        '8.4.1',
        ReleaseType.Hotfix,
        destinations,
        workflowStepGraph,
        ReleaseTimelinePhase.Completed,
        'Critical payment processing fix for international transactions',
        ReleaseStatus.Completed,
    ),
    // 8.4.0 - Skipped
    mockWorkflowRelease(
        appId,
        '8.4.0',
        ReleaseType.Minor,
        destinations,
        workflowStepGraph,
        ReleaseTimelinePhase.Completed,
        'Postponed to merge with enhanced baggage tracking feature',
        ReleaseStatus.Skipped,
    ),
    // 8.3.0 - Skipped
    mockWorkflowRelease(
        appId,
        '8.3.0',
        ReleaseType.Minor,
        destinations,
        workflowStepGraph,
        ReleaseTimelinePhase.Completed,
        'Deferred due to updated branding guidelines from partners',
        ReleaseStatus.Skipped,
    ),
    // 8.2.0 - Completed
    mockWorkflowRelease(
        appId,
        '8.2.0',
        ReleaseType.Minor,
        destinations,
        workflowStepGraph,
        ReleaseTimelinePhase.Completed,
        'Initial white label platform launch across all airline partners',
        ReleaseStatus.Completed,
    ),
    // 8.1.0 - Skipped
    mockWorkflowRelease(
        appId,
        '8.1.0',
        ReleaseType.Minor,
        destinations,
        workflowStepGraph,
        ReleaseTimelinePhase.Completed,
        'Beta testing extended for additional partner feedback',
        ReleaseStatus.Skipped,
    ),
    // 8.0.0 - Completed
    mockWorkflowRelease(
        appId,
        '8.0.0',
        ReleaseType.Minor,
        destinations,
        workflowStepGraph,
        ReleaseTimelinePhase.Completed,
        'Pre-launch milestone with all core features validated',
        ReleaseStatus.Completed,
    ),
];

export const whiteLabelAppReleasesMock: DemoReleasePayload[] = [];
